import { BookOpen, FileText } from 'lucide-react';
import { Link, useLocation, useParams } from 'react-router';

import { cn } from '../lib/utils';
import { useGetCourse } from '../query/course';
import { Tabs, TabsList, TabsTrigger } from './ui/tabs';

const UUID_PATTERN = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}';

const PATHS = [
  {
    regex: new RegExp(`^/course/${UUID_PATTERN}$`, 'i'),
    path: '과제',
  },
  {
    regex: new RegExp(`^/course/${UUID_PATTERN}/notice$`, 'i'),
    path: '공지사항',
  },
];

const getTab = (pathname: string) => {
  if (
    PATHS.find(data => {
      const { regex, path } = data;
      return regex.test(pathname) && path === '공지사항';
    })
  ) {
    return 'notice';
  }

  if (
    PATHS.find(data => {
      const { regex, path } = data;
      return regex.test(pathname) && path === '과제';
    })
  ) {
    return 'assignment';
  }

  return;
};

const CourseTabs = () => {
  const location = useLocation();
  const pathname = location.pathname;
  const { courseId } = useParams<{ courseId: string }>();
  const { data: course } = useGetCourse(courseId!);

  const matchedPath = PATHS.find(({ regex }) => regex.test(pathname));

  if (!course) return <></>;

  return (
    <>
      <div className="mb-8">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-gray-500 mb-1">
              {course?.data?.school} {course?.data?.department}
            </p>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              {course?.data?.name}{' '}
              <span className="text-gray-600">({course?.data?.subject.name})</span>
            </h1>
            <div className="flex items-center space-x-4 text-sm text-gray-500">
              <span>
                {course?.data?.year} {course?.data?.semester}
              </span>
              <span>-</span>
              <span>담당교수: {course?.data?.professor?.name}</span>
            </div>
          </div>
        </div>
      </div>
      {matchedPath && (
        <Tabs className="space-y-6 mb-8" value={getTab(pathname)}>
          <TabsList className="grid w-full grid-cols-2 lg:w-auto lg:grid-cols-2">
            <TabsTrigger value="notice" className="flex items-center space-x-2">
              <Link
                to={`/course/${courseId}/notice`}
                className="flex items-center justify-center space-x-2 w-full"
              >
                <FileText className="h-4 w-4" />
                <span>공지사항</span>
              </Link>
            </TabsTrigger>
            <TabsTrigger value="assignment" className="flex items-center space-x-2">
              <Link
                to={`/course/${courseId}`}
                className="flex items-center justify-center space-x-2 w-full"
              >
                <BookOpen className="h-4 w-4" />
                <span>과제</span>
              </Link>
            </TabsTrigger>
          </TabsList>
        </Tabs>
      )}
    </>
  );

  return (
    <div className="-m-4 block">
      <div className="text-sm px-4 py-4">
        <div>
          <span>{course?.data?.school}</span> <span>{course?.data?.department}</span>
        </div>
        <div className="flex gap-2 align-bottom items-end">
          <span className="text-base font-bold">{course?.data?.name}</span>
          <span className="text-base font-bold mr-4">({course?.data?.subject.name})</span>

          <span className="text-sm align-bottom">{course?.data?.year}</span>
          <span className="text-sm">{course?.data?.semester}</span>
        </div>
        <div></div>
      </div>
      <div className="w-full flex gap-4 border-b border-t border-[#e5e7eb] h-16 mb-4">
        <Link
          to={`/course/${courseId}/notice`}
          className={cn(
            'px-8 flex text-sm font-medium text-center items-center justify-center h-full hover:text-primary py-8 relative w-32',
            PATHS.find(data => {
              const { regex, path } = data;
              return regex.test(pathname) && path === '공지사항';
            })
              ? 'text-primary font-bold text-lg'
              : 'font-bold text-muted-foreground text-lg',
          )}
        >
          공지사항
          {PATHS.find(data => {
            const { regex, path } = data;
            return regex.test(pathname) && path === '공지사항';
          }) && (
            <div className="absolute bottom-[-1px] left-0 right-0 bg-black h-[4px] rounded-full" />
          )}
        </Link>
        <Link
          to={`/course/${courseId}`}
          className={cn(
            'px-8 flex text-sm font-medium text-center items-center justify-center h-full hover:text-primary py-8 relative w-32',
            PATHS.find(data => {
              const { regex, path } = data;
              return regex.test(pathname) && path === '과제';
            })
              ? 'text-primary font-bold text-lg'
              : 'font-bold text-muted-foreground text-lg',
          )}
        >
          과제
          {PATHS.find(data => {
            const { regex, path } = data;
            return regex.test(pathname) && path === '과제';
          }) && (
            <div className="absolute bottom-[-1px] left-0 right-0 bg-black h-[4px] rounded-full" />
          )}
        </Link>
      </div>
    </div>
  );
};

export default CourseTabs;
